// Pure URL helpers for useRoute. No React here, so they can be unit tested.

// Parse a query string ('a=1&b=2', no leading '?') into an object.
// URLSearchParams decodes keys and values, so '%20' (and the '+' that
// URLSearchParams.toString() writes) come back as a space. Without the decode,
// buildQueryString would encode '%' again on every navigation.
export const parseQueryString = (queryString: string) => {
  const query: { [key: string]: string } = {};

  new URLSearchParams(queryString).forEach((value, key) => {
    if (key) query[key] = value;
  });
  return query;
};

// Parse hash fragments into structured components
export const parseHash = (hashString: string) => {
  // Remove the leading '#' if it exists
  const cleanHash = hashString.startsWith('#') ? hashString.substring(1) : hashString;

  // Split into path and query parts
  const [path, queryString] = cleanHash.split('?');

  // Split the path by '/' and filter out empty strings
  const pathParts = path.split('/').filter((part: string) => part);

  // Extract name and uid
  const name = pathParts[0] || null;
  const uid = pathParts[1] || null;

  // Parse query parameters
  const query = parseQueryString(queryString || '');

  // Construct a clean path without hash symbol or query params
  const cleanPath = pathParts.length > 0 ? pathParts.join('/') : '';

  return { name, uid, query, cleanPath };
};

// Parse search string into query parameters
export const parseSearch = (searchString: string) => {
  const cleanSearch =
    searchString.startsWith('?') ? searchString.substring(1) : searchString;

  return parseQueryString(cleanSearch);
};

/** `query` with `changes` over it. A null value removes its key. */
export const mergeQuery = (
  query: { [key: string]: string | number | boolean },
  changes: { [key: string]: string | number | boolean | null },
): { [key: string]: string | number | boolean } => {
  const merged: { [key: string]: string | number | boolean } = { ...query };

  for (const [key, value] of Object.entries(changes)) {
    if (value === null) delete merged[key];
    else merged[key] = value;
  }
  return merged;
};

// Convert query object to query string
export const buildQueryString = (
  query: { [key: string]: string | number | boolean },
): string => {
  if (!query || Object.keys(query).length === 0) return '';

  return (
    '?' +
    Object.entries(query)
      .map(
        ([key, value]) =>
          `${encodeURIComponent(key)}=${encodeURIComponent(value ?? '')}`,
      )
      .join('&')
  );
};

// Normalise sidebar path for hash building
export const normalizeSidePath = (p: string) =>
  p.startsWith('/') ? p.substring(1) : p;

export const buildHash = (
  sidebarPathClean: string,
  sidebarQuery: Record<string, any>,
) => {
  if (!sidebarPathClean) return '';
  return `#/${sidebarPathClean}${buildQueryString(sidebarQuery)}`;
};

// The hash that navSidePath navigates to. The current sidebar query is kept.
// A query inside the new path ('fertigator/abc?tab=2') is merged over it,
// not appended as a second '?'.
export const buildSidePathHash = (
  newSidePath: string,
  currentSidebarQuery: Record<string, any>,
) => {
  const [path, queryString] = newSidePath.split('?');

  return buildHash(normalizeSidePath(path), {
    ...currentSidebarQuery,
    ...parseQueryString(queryString || ''),
  });
};

export interface SidePathOptions {
  // Drop the current sidebar query (for example ?tab=4) instead of keeping it.
  // A query inside the new path is still used.
  resetQuery?: boolean;
}

// The URL that navPage navigates to. It opens a new page: the current main
// query and the current sidebar are not kept. The URL can hold a main query
// ('/broker/x/trays?seasonId=1') and a sidebar ('/broker/x/seasons#/season/1').
export const buildPageUrl = (url: string) => {
  const hashIndex = url.indexOf('#');
  const beforeHash = hashIndex === -1 ? url : url.substring(0, hashIndex);
  const sidebar = parseHash(hashIndex === -1 ? '' : url.substring(hashIndex));
  const [path, queryString] = beforeHash.split('?');

  return (
    (path.startsWith('/') ? path : `/${path}`) +
    buildQueryString(parseQueryString(queryString || '')) +
    buildHash(sidebar.cleanPath, sidebar.query)
  );
};

// True for a left click with no modifier key. Use it in the onClick of an
// element that has an href: on a plain left click, call event.preventDefault()
// and navigate with useRoute. Let the browser handle any other click, so
// ctrl/cmd/shift-click opens the href in a new tab or window.
export const isPlainLeftClick = (event: {
  button: number;
  altKey: boolean;
  ctrlKey: boolean;
  metaKey: boolean;
  shiftKey: boolean;
}) =>
  event.button === 0 &&
  !event.altKey &&
  !event.ctrlKey &&
  !event.metaKey &&
  !event.shiftKey;

// The full URL that navSidePath navigates to. The main path and the main
// query do not change. An empty side path closes the sidebar.
export const buildSidePathUrl = (
  location: { pathname: string; search: string; hash: string },
  newSidePath: string,
  options: SidePathOptions = {},
) => {
  const currentSidebarQuery = options.resetQuery ? {} : parseHash(location.hash).query;

  return (
    location.pathname +
    location.search +
    buildSidePathHash(newSidePath, currentSidebarQuery)
  );
};
